import React, { useCallback, useEffect, useRef } from 'react';
import { useStore } from '../../store/store';
import { EditorState, Modifier, SelectionState } from 'draft-js';

// Li depth className to retrieve depth
const baseLiClassName = 'public-DraftStyleDefault-depth';
// Margin left's multiplier factors corresponding to li depth
const depthMarginCorrelation = {
  [`${baseLiClassName}0`]: 1.5,
  [`${baseLiClassName}1`]: 3,
  [`${baseLiClassName}2`]: 4.5,
  [`${baseLiClassName}3`]: 6,
  [`${baseLiClassName}4`]: 7.5,
};

const EditorWrapper = ({ children, boxId, editorState, setEditorState, box, textStyle }) => {
  const ref = useRef();
  const configurationFontSize = useStore((state) => state.configuration.fontSize)
  const wrapperId = `${boxId.split('.').join("")}-editor-wrapper`
  const { searchResults, searchIndex } = useStore((state) => ({
    searchResults: state.searchResults,
    searchIndex: state.searchIndex,
  }));

  const stylizeLis = useCallback(
    (lis, isOl) => {
      lis?.forEach((li) => {
        // Retrieve block offsetKey
        const offsetKey = li.dataset.offsetKey;
        if (!offsetKey) return;

        // Retrieve first span before the marker/before to get the first
        // character's style
        const firstSpan = li.querySelector(
          `span[data-offset-key="${offsetKey}"]`
        );
        if (!firstSpan) return;
        const styleToCopy = firstSpan.style;

        // Find or create style for this li
        let styleElem = document.getElementById(`style-${offsetKey}-${wrapperId}`);
        if (!styleElem) {
          styleElem = document.createElement('style');
          styleElem.id = `style-${offsetKey}-${wrapperId}`;
          document.head.appendChild(styleElem);
        }

        // Apply first character's style to marker or before
        let innerHtml = `#${wrapperId}
          li[data-offset-key="${offsetKey}"] {.public-DraftStyleDefault-block::before {font-size: ${styleToCopy['font-size']};color: ${styleToCopy['color']};}}
          li[data-offset-key="${offsetKey}"]::marker {font-size: ${styleToCopy['font-size']};color: ${styleToCopy['color']};}
          li[data-offset-key="${offsetKey}"]::before {font-size: ${styleToCopy['font-size']};color: ${styleToCopy['color']}; font-family: ${styleToCopy['font-family']};font-weight: ${styleToCopy['font-weight']};font-style: ${styleToCopy['font-style']};text-decoration: ${styleToCopy['text-decoration']};}
          `;
        styleElem.innerHTML = innerHtml;

        // Put corresponding margin-left on li depending on li depth and font-size
        if (styleToCopy['font-size']) {
          const fontSize = parseFloat(styleToCopy['font-size']);
          // Retrieve li depth className
          const liDepthClassName = Array.from(li.classList).find((className) =>
            className.includes(baseLiClassName)
          );
          // Retrieve li margin-left's multiplier factor
          const marginUsed = depthMarginCorrelation[liDepthClassName];
          // Add difference between current fontSize and configurationFontSize
          // to default margin-left (pixelsToAdd is always positive)
          const pixelsToAdd = Math.max(0, fontSize - configurationFontSize);
          li.style['margin-left'] = `${marginUsed * configurationFontSize + pixelsToAdd
            }px`;
        }
      });
    },
    [configurationFontSize, wrapperId]
  );

  useEffect(() => {
    const uls = ref.current.querySelectorAll('ul');
    uls?.forEach((ul) => {
      const lis = ul.querySelectorAll('li');
      stylizeLis(lis, false);
    });
    const ols = ref.current.querySelectorAll('ol');
    ols?.forEach((ul) => {
      const lis = ul.querySelectorAll('li');
      stylizeLis(lis, true);
    });
  }, [editorState, stylizeLis]);

  // DOM-based highlight: robust to re-renders, no Draft state mutation
  useEffect(() => {
    // Décaler pour laisser Draft rendre les blocs après changements d'état
    const timer = setTimeout(() => {
      // Pour les tableaux, le boxId peut être de la forme `${tableBoxId}-${columnKey}` ou `${tableBoxId}-${columnKey}-${rowKey}`
      // On doit matcher les résultats de recherche qui correspondent à cette cellule/header
      const boxRanges = searchResults.filter((r) => {
        if (!r.isTable) {
          // Pour les boxes de texte normales et les titres, correspondance directe
          return r.boxId === boxId;
        }
        // Pour les tableaux, construire l'ID attendu et comparer
        // Le boxId dans EditorWrapper pour les headers est: `${tableBoxId}-${columnKey}`
        // Le boxId dans EditorWrapper pour les cellules est: `${tableBoxId}-${columnKey}-${rowKey}`
        if (r.isHeader) {
          // Header: boxId devrait être `${r.boxId}-${r.columnKey}`
          return boxId === `${r.boxId}-${r.columnKey}`;
        } else {
          // Cellule: boxId devrait être `${r.boxId}-${r.columnKey}-${r.rowKey}`
          return boxId === `${r.boxId}-${r.columnKey}-${r.rowKey}`;
        }
      });

      // Pour les titres, le wrapper peut être le div avec id={boxId} directement
      // Pour les autres, c'est le wrapper avec l'ID `${boxId}-editor-wrapper`
      let wrapper = document.getElementById(wrapperId);
      if (!wrapper) {
        // Essayer avec l'ID direct (pour les titres)
        wrapper = document.getElementById(boxId);
      }
      if (!wrapper) return;

      // 1) Clear previous highlights
      const existing = wrapper.querySelectorAll('span.search-highlight');
      existing.forEach((el) => {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
        parent.normalize();
      });

      if (boxRanges.length === 0) return;

      // 2) Apply new highlights per block
      const byBlock = boxRanges.reduce((acc, r) => {
        (acc[r.blockKey] = acc[r.blockKey] || []).push(r);
        return acc;
      }, {});

      Object.entries(byBlock).forEach(([blockKey, ranges]) => {
        // data-offset-key commence par `${blockKey}-...`
        const blockRoot = wrapper.querySelector(`[data-offset-key^="${blockKey}-"]`);
        if (!blockRoot) return;

        // Construire une liste plate des nœuds de texte avec offsets cumulés
        const walker = document.createTreeWalker(blockRoot, NodeFilter.SHOW_TEXT, null);
        const textNodes = [];
        let node, cum = 0;
        while ((node = walker.nextNode())) {
          const len = node.textContent.length;
          if (len > 0) {
            textNodes.push({ node, start: cum, end: cum + len });
            cum += len;
          }
        }
        if (textNodes.length === 0) return;

        // Pour chaque plage, repérer les nœuds chevauchés et wrapper
        ranges.forEach(({ start, end }) => {
          textNodes.forEach(({ node, start: ns, end: ne }) => {
            const overlapStart = Math.max(ns, start);
            const overlapEnd = Math.min(ne, end);
            if (overlapStart < overlapEnd) {
              const localStart = overlapStart - ns;
              const localEnd = overlapEnd - ns;
              const text = node.textContent;
              const before = text.slice(0, localStart);
              const match = text.slice(localStart, localEnd);
              const after = text.slice(localEnd);
              const parent = node.parentNode;
              const frag = document.createDocumentFragment();
              if (before) frag.appendChild(document.createTextNode(before));
              const span = document.createElement('span');
              span.className = 'search-highlight';
              span.style.backgroundColor = 'rgba(12, 180, 90, 0.35)'; // vert par défaut
              span.textContent = match;
              // stocker les métadonnées pour identifier l'occurrence active
              span.dataset.blockKey = blockKey;
              span.dataset.start = String(overlapStart);
              span.dataset.end = String(overlapEnd);
              frag.appendChild(span);
              if (after) frag.appendChild(document.createTextNode(after));
              parent.replaceChild(frag, node);
            }
          });
        });
      });

      // 3) Mettre en surbrillance orange l'occurrence active pour cette box
      const state = require('../../store/store').useStore.getState();
      const active = state.searchResults?.[state.searchIndex];
      if (active) {
        // Vérifier si ce résultat actif correspond à cette box/cellule/header
        let matches = false;
        if (!active.isTable) {
          matches = active.boxId === boxId;
        } else {
          if (active.isHeader) {
            matches = boxId === `${active.boxId}-${active.columnKey}`;
          } else {
            matches = boxId === `${active.boxId}-${active.columnKey}-${active.rowKey}`;
          }
        }

        if (matches) {
          const activeSpan = wrapper.querySelector(
            `span.search-highlight[data-block-key="${active.blockKey}"][data-start="${active.start}"][data-end="${active.end}"]`
          );
          if (activeSpan) {
            activeSpan.style.backgroundColor = 'rgba(255, 165, 0, 0.5)'; // orange
            // Vérifier si l'élément est déjà visible avant de scroller
            // On considère qu'un élément est visible s'il est au moins partiellement dans la viewport
            const rect = activeSpan.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const isVisible = (
              rect.top < viewportHeight &&
              rect.bottom > 0 &&
              rect.left < viewportWidth &&
              rect.right > 0
            );
            // Ne scroller que si l'élément n'est pas déjà visible (même partiellement)
            if (!isVisible) {
              activeSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [searchResults, searchIndex, boxId, wrapperId]);

  useEffect(() => {
    if (!box) return;
    // Passe par ref.current (pas getElementById(wrapperId)) : le même boxId est
    // rendu à la fois dans le canvas et dans l'aperçu PDF (PreviewOverlay), qui
    // restent tous deux montés en même temps → id DOM dupliqué. getElementById
    // renvoie toujours la 1ère occurrence (le canvas), donc l'aperçu ne
    // recevait jamais ce style, d'où le justify perdu dans l'aperçu.
    const editorWrapper = ref.current;
    if (!editorWrapper) return;

    const draftStyleDefaultBlocks = editorWrapper.querySelectorAll(".public-DraftStyleDefault-block");

    draftStyleDefaultBlocks.forEach(block => {
      const alignment = box.content.alignment || textStyle.positions.horizontal;

      if (alignment === 'justify') {
        block.style.justifyContent = 'flex-start';
        block.style.textAlign = 'justify';
      } else {
        block.style.justifyContent = alignment;
        block.style.textAlign = '';
      }
    });
  }, [box?.content.alignment, box?.content?.imagePosition, boxId, textStyle?.imagePosition, textStyle?.positions.horizontal, box]);

  useEffect(() => {
    if (!box) return;

    const rafId = requestAnimationFrame(() => {
      const editorWrapper = document.getElementById(`${boxId.split('.').join("")}-editor-wrapper`);
      if (!editorWrapper) return;

      const spans = editorWrapper.querySelectorAll("span");
      const pos = box.content?.imagePosition || textStyle?.imagePosition;
      const positionMap = {
        center: "center center",
        start: "top center",
        end: "bottom center",
      };

      spans.forEach(span => {
        span.style.alignSelf = pos;
        if (positionMap[pos]) {
          span.style.backgroundPosition = positionMap[pos];
        }
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [box?.content?.imagePosition, textStyle?.imagePosition, editorState, boxId, box]);

  return (
    <div ref={ref} id={wrapperId}>
      {children}
    </div>
  )
}

export default EditorWrapper
